/**
 * FlowStrip — the pipeline flow-through breadcrumb.
 *
 * Drop it on any page in the client → request → assessment → backend quote →
 * client quote → takeoff → job → invoices chain, hand it the ONE id that page
 * already knows, and it resolves + renders the whole chain as clickable chips
 * (via /api/flow action:chain). Stages that don't exist yet render dimmed.
 *
 *   <FlowStrip anchor={{ assessment: assessmentId }} />
 *   <FlowStrip anchor={{ quote: id }} />
 *   <FlowStrip anchor={{ sheet: sheetId }} />
 *   <FlowStrip anchor={{ job: jobNum }} />
 *   <FlowStrip anchor={{ request: requestId }} />
 *
 * In-app stages navigate in-app; Jobber-only stages (client, client-facing
 * quote) open Jobber in a new tab and are marked ↗. The job/invoice chips
 * fall back to Jobber links for crew without the Jobs permission (the /job
 * route and the money fields are gated the same way as the Jobs tab).
 *
 * It also carries the owner-only 🔬 Audit button (AuditTraceButton) — because
 * the strip is already mounted on every stage with that stage's anchor, this is
 * the one place a "capture the whole workflow from here" control can live once
 * and appear everywhere.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../lib/apiFetch";
import { usePermissions } from "../hooks/usePermissions";
import AuditTraceButton from "./AuditTraceButton";

const C = {
  surface: "#161a20", card: "#1c2028", border: "#2a3140",
  lime: "#a5ce3a", text: "#e8eaf0", muted: "#8892a4",
};

const money = (n) => (n == null || isNaN(+n) ? null : "$" + Math.round(+n).toLocaleString("en-CA"));
const trunc = (s, n) => { const t = String(s || "").trim(); return t.length > n ? t.slice(0, n - 1) + "…" : t; };

// anchor prop key → /api/flow body key + which chip to highlight
const ANCHOR_KEYS = { assessment: "assessmentId", quote: "quoteId", sheet: "sheetId", job: "jobNum", request: "requestId" };

export default function FlowStrip({ anchor, style }) {
  const navigate = useNavigate();
  const { isAdmin } = usePermissions() || {};
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | hidden

  const anchorType = Object.keys(ANCHOR_KEYS).find((k) => anchor?.[k]);
  const anchorValue = anchorType ? anchor[anchorType] : null;

  useEffect(() => {
    let dead = false;
    if (!anchorType || !anchorValue) { setState("hidden"); return; }
    setState("loading");
    (async () => {
      try {
        const r = await apiFetch("/api/flow", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "chain", [ANCHOR_KEYS[anchorType]]: anchorValue }),
        });
        const j = await r.json();
        if (dead) return;
        if (!r.ok || j.ok === false) { setState("hidden"); return; }
        setData(j);
        setState("ready");
      } catch { if (!dead) setState("hidden"); }
    })();
    return () => { dead = true; };
  }, [anchorType, anchorValue]);

  if (state === "hidden") return null;

  const s = data?.stages || {};
  const canJobs = !!data?.canJobs;
  const openExt = (uri) => uri && window.open(uri, "_blank", "noopener");

  // "takeoff" highlights from a sheet anchor; "quote" from a quote anchor; etc.
  const currentChip = anchorType === "sheet" ? "takeoff" : anchorType;

  const chips = [
    {
      key: "client", label: "Client", stage: s.client, ext: true,
      value: s.client ? trunc(s.client.name, 22) : null,
      onClick: s.client ? () => openExt(s.client.webUri) : null,
    },
    {
      key: "request", label: "Request", stage: s.request,
      value: s.request ? (trunc(s.request.title, 22) || s.request.status || "open") : null,
      sub: s.request?.status || null,
      onClick: s.request ? () => navigate(`/?request=${encodeURIComponent(s.request.id)}`) : null,
    },
    {
      key: "assessment", label: "Assessment", stage: s.assessment,
      value: s.assessment
        ? [(s.assessment.serviceType || "assessment").replace(/_/g, " "), s.assessment.count > 1 ? `×${s.assessment.count}` : null].filter(Boolean).join(" ")
        : null,
      sub: s.assessment?.state ? String(s.assessment.state).replace(/_/g, " ") : null,
      onClick: s.assessment ? () => navigate(`/assess/smart/${s.assessment.id}`) : null,
    },
    {
      key: "quote", label: "Backend Quote", stage: s.quote,
      value: s.quote ? (money(s.quote.total) || String(s.quote.state || "draft").replace(/_/g, " ")) : null,
      sub: s.quote && money(s.quote.total) ? String(s.quote.state || "").replace(/_/g, " ") : null,
      onClick: s.quote ? () => navigate(`/quote/${s.quote.id}`) : null,
    },
    {
      key: "jobberQuote", label: "Client Quote", stage: s.jobberQuote, ext: true,
      value: s.jobberQuote ? (s.jobberQuote.number ? `#${s.jobberQuote.number}` : "in Jobber") : null,
      sub: s.jobberQuote ? (s.jobberQuote.pushedAt ? "pushed" : s.jobberQuote.status || null) : null,
      onClick: s.jobberQuote?.webUri ? () => openExt(s.jobberQuote.webUri) : null,
    },
    {
      key: "takeoff", label: "Takeoff", stage: s.takeoff,
      value: s.takeoff ? (s.takeoff.jobNumber ? `Job #${s.takeoff.jobNumber}` : trunc(s.takeoff.title, 18) || "sheet") : null,
      onClick: s.takeoff ? () => navigate(`/takeoff?sheet=${s.takeoff.id}`) : null,
    },
    {
      key: "job", label: "Job", stage: s.job, ext: !canJobs,
      value: s.job ? `#${s.job.jobNum}` : null,
      sub: s.job?.status ? String(s.job.status).replace(/_/g, " ") : null,
      onClick: s.job
        ? (canJobs ? () => navigate(`/job/${s.job.jobNum}`) : (s.job.webUri ? () => openExt(s.job.webUri) : null))
        : null,
    },
    ...(canJobs ? [{
      key: "invoices", label: "Invoices", stage: s.invoices,
      value: s.invoices ? (s.invoices.count ? `${s.invoices.count} invoice${s.invoices.count > 1 ? "s" : ""}` : (s.invoices.invoiced != null ? money(s.invoices.invoiced) : "none yet")) : null,
      sub: s.invoices
        ? (s.invoices.outstanding > 0 ? `${money(s.invoices.outstanding)} owing` : (s.invoices.status ? String(s.invoices.status).replace(/_/g, " ") : null))
        : null,
      onClick: s.invoices ? () => navigate(`/job/${s.invoices.jobNum}?focus=invoices`) : null,
    }] : []),
  ];

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 4, padding: "8px 10px",
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
      fontFamily: "'Roboto', sans-serif", ...style,
    }}>
      {/* Chips scroll; the audit button sits outside the scroller so it stays
          reachable on a phone without swiping to the end of the chain. */}
      <div style={{ display: "flex", alignItems: "stretch", gap: 4, overflowX: "auto", flex: 1, minWidth: 0, WebkitOverflowScrolling: "touch" }}>
      {state === "loading" ? (
        <div style={{ color: C.muted, fontSize: 12, padding: "6px 4px", whiteSpace: "nowrap" }}>Loading pipeline…</div>
      ) : chips.map((chip, i) => {
        const present = !!chip.stage;
        const isCurrent = chip.key === currentChip;
        const clickable = present && !!chip.onClick && !isCurrent;
        return (
          <div key={chip.key} style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
            {i > 0 && <span style={{ color: present ? "#4a5568" : "#2d3441", fontSize: 13 }}>›</span>}
            <div
              onClick={clickable ? chip.onClick : undefined}
              title={present ? (isCurrent ? "You are here" : chip.ext ? "Open in Jobber" : undefined) : "Not created yet"}
              style={{
                padding: "4px 10px", borderRadius: 8, whiteSpace: "nowrap",
                cursor: clickable ? "pointer" : "default",
                background: isCurrent ? "rgba(165,206,58,.14)" : present ? C.card : "transparent",
                border: `1px solid ${isCurrent ? "rgba(165,206,58,.55)" : present ? C.border : "#232936"}`,
                opacity: present ? 1 : 0.45,
              }}
              onMouseEnter={(e) => { if (clickable) e.currentTarget.style.borderColor = "rgba(165,206,58,.5)"; }}
              onMouseLeave={(e) => { if (clickable) e.currentTarget.style.borderColor = C.border; }}
            >
              <div style={{ fontSize: 9, letterSpacing: ".08em", textTransform: "uppercase", color: isCurrent ? C.lime : C.muted, fontWeight: 700 }}>
                {chip.label}{present && chip.ext ? " ↗" : ""}
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: present ? C.text : C.muted, lineHeight: 1.35 }}>
                {present ? chip.value || "—" : "—"}
                {present && chip.sub ? <span style={{ color: C.muted, fontWeight: 400 }}> · {chip.sub}</span> : null}
              </div>
            </div>
          </div>
        );
      })}
      </div>
      {/* Owner-only: capture this whole run for a full audit. Lives here so it
          is on every stage automatically, anchored to whatever that page knows. */}
      {isAdmin && <AuditTraceButton anchor={anchor} />}
    </div>
  );
}
